import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useAuthService } from "../../Context/AuthContext";
import { useIsSpeaking } from "@livekit/components-react";
import { ReactComponent as NotSpeakingParticipantIcon } from "../../Assets/iconsv2/controlButtons/participantNotSeakingIcon.svg"
import { useTranslate } from '@tolgee/react';
import AudioWaveformBadge, { MiniWave } from "../GenericComponents/AudioWaveformBadge";
import { RaisedHandIcon } from "./RaisedHands";
import { colors } from "../../Assets/styles/colors";
import {
  AvatarWrapper, Status, Name, VideoContainer,
  NameBadge, NameInfo, NameBadgeText, StatusRow, StatusText, StatusAmp, MutedIconWrapper,
} from "./ParticipantSharedStyles";


const ParticipantComponentFocusedTranslationEnabled = ({
    participant,
    callInfo,
    grid,
    focused,
}) => {
    const { t } = useTranslate();
    const divRef = useRef(null);
    let tracks = participant.getTrackPublications();
    const [vTrack, setVTrack] = useState(null);
    const videoRef = useRef(null);
    const [participantInfoFromCall, setParticipantInfoFromCall] = useState(null);
    const { user } = useAuthService();
    const isUserspeaking = useIsSpeaking(participant);
    const [isEmotiiSpeaking, setIsEmotiiSpeaking] = useState(false);
    const [fontSize, setFontSize] = useState(16);
    const [width, setWidth] = useState(300);
    const [avatarSize, setAvatarSize] = useState(80);
    const [mutedMicrophone, setMutedMicrophone] = useState(false);

    useEffect(() => {
        const onStart = (e) => {
            if (String(e.detail?.userId) === String(participant.identity)) {
                setIsEmotiiSpeaking(true);
            }
        };
        const onEnd = (e) => {
            if (String(e.detail?.userId) === String(participant.identity)) {
                setIsEmotiiSpeaking(false);
            }
        };
        window.addEventListener("emotii-playback-start", onStart);
        window.addEventListener("emotii-playback-end",   onEnd);
        return () => {
            window.removeEventListener("emotii-playback-start", onStart);
            window.removeEventListener("emotii-playback-end",   onEnd);
        };
    }, [participant.identity]);

    const getParticipantInfoFromCall = () => {
        let info;
        let foundInOthers = callInfo.other_participants.find(
            (p) => p.id == participant.identity
        );

        if (participant.identity == callInfo.created_by.id) {
            info = callInfo.created_by;
        } else if (foundInOthers) {
            info = foundInOthers;
        } else {
            info = user;
        }

        if (info) {
            setParticipantInfoFromCall(info);
        }
    };


    useEffect(() => {
        const allTracks = participant.getTrackPublications();
        const audioTrackPub = allTracks.find(
            (track) => track.source === "microphone" && track.track != null
        );

        const handleMuted = () => setMutedMicrophone(true);
        const handleUnmuted = () => setMutedMicrophone(false);

        const handleTrackSubscribed = (track) => {
            if (track.kind === 'audio') {
                setMutedMicrophone(track.isMuted);
                track.on('muted', handleMuted);
                track.on('unmuted', handleUnmuted);
            }
        };

        let audioTrack;

        if (audioTrackPub && audioTrackPub.track) {
            audioTrack = audioTrackPub.track;
            setMutedMicrophone(audioTrack.isMuted);
            audioTrack.on('muted', handleMuted);
            audioTrack.on('unmuted', handleUnmuted);
        }

        participant.on('trackSubscribed', handleTrackSubscribed);

        return () => {
            participant.off('trackSubscribed', handleTrackSubscribed);

            if (audioTrack) {
                audioTrack.off('muted', handleMuted);
                audioTrack.off('unmuted', handleUnmuted);
            }
        };
    }, [participant]);

    useEffect(() => {
        let videoTrack = tracks.find(
            (track) => track.source === "camera" && track.track != null
        );

        if (videoTrack && videoTrack?.track) {
            setVTrack(videoTrack);
            videoTrack.track.attach(videoRef.current);
        } else {
            setVTrack(null);
        }
    }, [tracks]);

    useEffect(() => {
        getParticipantInfoFromCall();
    }, [participant]);

    useEffect(() => {
        const adjustFontSize = () => {
            if (divRef.current) {
                const { width, height } = divRef.current.getBoundingClientRect();
                setFontSize(Math.min(width / 10, height / 10, 40));
                setWidth(width);
                setAvatarSize(Math.min(width / 3, height / 3, 80));
            }
        };

        adjustFontSize();
        window.addEventListener("resize", adjustFontSize);
        return () => window.removeEventListener("resize", adjustFontSize);
    }, []);

    return (
        <ConnectingContainer
            ref={divRef}
            speaking={isUserspeaking}
            isEmotiiSpeaking={isEmotiiSpeaking}
            $isBoth={isUserspeaking && isEmotiiSpeaking}
        >
            <RaisedHandIcon identity={participant.identity} />
            {vTrack ? (
                <>
                <VideoContainer>
                    <video ref={videoRef} autoPlay muted playsInline />
                    {mutedMicrophone &&
                        <MutedIconWrapper>
                            <NotSpeakingParticipantIcon />
                        </MutedIconWrapper>
                    }
                </VideoContainer>
                <NameBadge $speaking={isUserspeaking} $emotii={isEmotiiSpeaking}>
                    {(isUserspeaking || isEmotiiSpeaking) &&
                        <MiniWave isEmotii={isEmotiiSpeaking} isBoth={isUserspeaking && isEmotiiSpeaking} />
                    }
                    <NameInfo>
                        <NameBadgeText $speaking={isUserspeaking} $emotii={isEmotiiSpeaking && !isUserspeaking}>
                            {participantInfoFromCall?.name}
                        </NameBadgeText>
                        <StatusRow>
                            {isUserspeaking && (
                                <StatusText $emotii={false}>{t("call_modal.speaking") + "..."}</StatusText>
                            )}
                            {isUserspeaking && isEmotiiSpeaking && <StatusAmp>&amp;</StatusAmp>}
                            {isEmotiiSpeaking && (
                                <StatusText $emotii={true}>{t("call_modal.interpreting")}</StatusText>
                            )}
                        </StatusRow>
                    </NameInfo>
                </NameBadge>
                </>
            ) : (
                <AvatarWrapper>
                    <AudioWaveformBadge
                        participant={participantInfoFromCall}
                        isSpeaking={isUserspeaking}
                        isEmotiiSpeaking={isEmotiiSpeaking}
                        size={avatarSize}
                    />
                    <Name fontSize={fontSize}>{participantInfoFromCall?.name}</Name>
                    {width > 300 && (
                        isUserspeaking && isEmotiiSpeaking ? (
                            <StatusRow>
                                <Status $speaking={true}>{t("call_modal.speaking") + "..."}</Status>
                                <StatusAmp $large>&amp;</StatusAmp>
                                <Status $emotii={true}>{t("call_modal.interpreting")}</Status>
                            </StatusRow>
                        ) : (
                            <Status $speaking={isUserspeaking} $emotii={isEmotiiSpeaking}>
                                {isEmotiiSpeaking
                                    ? t("call_modal.interpreting")
                                    : isUserspeaking
                                        ? t("call_modal.speaking") + "..."
                                        : t("call_modal.connected")}
                            </Status>
                        )
                    )}
                    {mutedMicrophone &&
                        <MutedIconWrapper>
                            <NotSpeakingParticipantIcon />
                        </MutedIconWrapper>
                    }
                </AvatarWrapper>
            )}
        </ConnectingContainer>
    );
};

export default ParticipantComponentFocusedTranslationEnabled;

const ConnectingContainer = styled.div`
  display: flex;
  flex: 1 0 calc(25% - 10px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.callComponent.participantComponentBackground};
  overflow: hidden;
  border-radius: 4px;
  position: relative;
  min-height: 300px;
  border: 2px solid ${({ speaking, isEmotiiSpeaking }) =>
    isEmotiiSpeaking ? colors.emotiiPurple
    : speaking ? colors.speakingGreen
    : "transparent"};
  ${({ $isBoth, theme }) => $isBoth && css`
    border-color: transparent;
    background:
      linear-gradient(${theme.callComponent.participantComponentBackground}, ${theme.callComponent.participantComponentBackground}) padding-box,
      linear-gradient(135deg, ${colors.speakingGreen} 50%, ${colors.emotiiPurple} 50%) border-box;
  `}
  transition: border-color 0.3s ease, background 0.3s ease;
`;

const SpeakingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 20px;
  left: 20px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${({ theme }) => theme.callComponent.speakingLabelBg};
  padding: 5px 10px;
  p {
    font-size: 16px;
    font-weight: 500;
    line-height: 18px;
    color: ${({ $emotii, theme }) =>
      $emotii ? colors.emotiiPurple : theme.menu.primaryTextColor};
  }
`;
